import React, { useState, Fragment, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, Transition } from '@headlessui/react';
import api, { FilterPreferences } from '../../services/api';
import BulkCreateIncidentModal from '../../components/BulkCreateIncidentModal';
import FilterDropdown, { FilterSection } from '../../components/FilterDropdown';
import ActiveFilterChips, { FilterChip } from '../../components/ActiveFilterChips';
import TableColumnFilter from '../../components/TableColumnFilter';
import { useListLayoutPreferences } from '../../hooks/useListLayoutPreferences';
import { useFilterPreferences } from '../../hooks/useFilterPreferences';
import { useTicketEvents } from '../../hooks/useTicketEvents';
import { getDurationPresetRange, DurationPreset } from '../dashboard/utils/dateHelpers';
import ListLayoutSettingsModal from '../../components/ListLayoutSettingsModal';
import ListLayoutSettingsButton from '../../components/ListLayoutSettingsButton';
import RefreshButton from '../../components/RefreshButton';
import GenericExportButton from '../../components/GenericExportButton';
import { ExportConfigs } from '../../services/genericExportService';
import { useAuth } from '../../contexts/AuthContext';
import { trackIncidentOperation } from '../../utils/appInsights';
import { useIsServiceDeskUser } from '../../hooks/useIsServiceDeskUser';
import { usePersistListFiltersSession } from '../../hooks/usePersistListFiltersSession';
import { LIST_FILTER_SESSION_KEYS } from '../../constants/listFilterSessionKeys';
import { isTicketSearchInputValid } from '../../utils/searchTerms';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
  DocumentPlusIcon,
  EnvelopeIcon,
  ArrowUturnRightIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import EmailTicketModal from '../../components/EmailTicketModal';
import SurveyConfirmationModal from '../../components/SurveyConfirmationModal';

// Helper function to strip HTML tags and decode HTML entities
const stripHtml = (html: string): string => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

// SLA Status display component with icons and tooltips
interface SlaStatusItem {
  targetType: string;
  currentState: string;
  isBreached: boolean;
}

const SlaStatusDisplay: React.FC<{ slaStatus?: SlaStatusItem[] }> = ({ slaStatus }) => {
  if (!slaStatus || slaStatus.length === 0) {
    return <span className="text-gray-400">-</span>;
  }

  const getStatusIcon = (item: SlaStatusItem) => {
    if (item.isBreached || item.currentState === 'BREACHED') return '🔴';
    if (item.currentState === 'ACHIEVED' || item.currentState === 'COMPLETED') return '✅';
    if (item.currentState === 'RUNNING') return '⏳';
    if (item.currentState === 'PAUSED') return '⏸️';
    if (item.currentState === 'CANCELLED') return '⚪';
    return '⏳';
  };

  const getTargetLabel = (targetType: string) => {
    switch (targetType) {
      case 'RESPONSE': return 'R';
      case 'ONGOING': return 'O';
      case 'RESOLUTION': return 'S';
      default: return targetType.charAt(0);
    }
  };

  const getFullTargetName = (targetType: string) => {
    switch (targetType) {
      case 'RESPONSE': return 'Response SLA';
      case 'ONGOING': return 'Ongoing SLA';
      case 'RESOLUTION': return 'Resolution SLA';
      default: return targetType;
    }
  };

  const getStatusText = (item: SlaStatusItem) => {
    if (item.isBreached || item.currentState === 'BREACHED') return 'Breached';
    if (item.currentState === 'ACHIEVED' || item.currentState === 'COMPLETED') return 'Met';
    if (item.currentState === 'RUNNING') return 'Running';
    if (item.currentState === 'PAUSED') return 'Paused';
    if (item.currentState === 'CANCELLED') return 'Cancelled';
    return item.currentState;
  };

  const sortOrder = { 'RESPONSE': 1, 'ONGOING': 2, 'RESOLUTION': 3 };
  const sortedStatus = [...slaStatus].sort((a, b) =>
    (sortOrder[a.targetType as keyof typeof sortOrder] || 99) -
    (sortOrder[b.targetType as keyof typeof sortOrder] || 99)
  );

  return (
    <div className="flex items-center gap-1">
      {sortedStatus.map((item, index) => (
        <div key={item.targetType} className="relative group inline-flex items-center">
          <span className="cursor-help text-xs font-medium text-gray-600">{getTargetLabel(item.targetType)}</span>
          <span className="text-sm ml-0.5">{getStatusIcon(item)}</span>
          {index < sortedStatus.length - 1 && <span className="text-gray-300 mx-0.5">|</span>}
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
            {getFullTargetName(item.targetType)}: {getStatusText(item)}
            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Truncated text with tooltip component
const TruncatedTextWithTooltip: React.FC<{ text: string | null | undefined; maxLength?: number }> = ({ text, maxLength = 50 }) => {
  if (!text) {
    return <span className="text-gray-400">-</span>;
  }

  const strippedText = stripHtml(text);
  const firstLine = strippedText.split('\n')[0];
  const displayText = firstLine.length > maxLength ? firstLine.substring(0, maxLength) + '...' : firstLine;
  const needsTooltip = strippedText.length > maxLength || strippedText.includes('\n');

  if (!needsTooltip) {
    return <span>{displayText}</span>;
  }

  return (
    <div className="relative group">
      <span className="cursor-help">{displayText}</span>
      <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded max-w-md whitespace-pre-wrap break-all opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-lg">
        {strippedText}
        <div className="absolute top-full left-4 border-4 border-transparent border-t-gray-800"></div>
      </div>
    </div>
  );
};

interface Incident {
  id: string;
  _id?: string;
  number: string;
  shortDescription: string;
  description?: string;
  priority: string;
  state: string;
  category?: string;
  ticketType: string;
  relatedRecordsCount?: number;
  assignedTo?: {
    id?: string;
    _id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    name?: string;
  } | null;
  caller?: {
    id?: string;
    _id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    name?: string;
  } | null;
  Customer?: {
    id?: string;
    accountName?: string;
    accountShortName?: string;
    emailAddress?: string;
  } | null;
  Engagement?: {
    id?: string;
    name?: string;
    status?: string;
  } | null;
  createdBy?: {
    id?: string;
    _id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    name?: string;
  } | null;
  createdAt: string;
  updatedAt: string;
  slaStatus?: Array<{
    targetType: string;
    currentState: string;
    isBreached: boolean;
  }>;
  surveySent?: boolean;
  surveyEligible?: boolean;
}

const priorityColors: Record<string, string> = {
  'critical_1': 'bg-red-100 text-red-800 border-red-200',
  'high_2': 'bg-orange-100 text-orange-800 border-orange-200',
  'moderate_3': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'low_4': 'bg-blue-100 text-blue-800 border-blue-200',
  'planning_5': 'bg-gray-100 text-gray-800 border-gray-200',
  // Legacy support
  CRITICAL: 'bg-red-100 text-red-800 border-red-200',
  HIGH: 'bg-orange-100 text-orange-800 border-orange-200',
  MEDIUM: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  LOW: 'bg-blue-100 text-blue-800 border-blue-200',
  NORMAL: 'bg-gray-100 text-gray-800 border-gray-200'
};

const priorityLabels: Record<string, string> = {
  'critical_1': 'Critical',
  'high_2': 'High',
  'moderate_3': 'Moderate',
  'low_4': 'Low',
  'planning_5': 'Planning',
  // Legacy support
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
  NORMAL: 'Planning'
};

const stateIcons = {
  new: <ClockIcon className="h-5 w-5 text-blue-500" />,
  in_progress: <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />,
  on_hold: <ClockIcon className="h-5 w-5 text-orange-500" />,
  resolved: <CheckCircleIcon className="h-5 w-5 text-green-500" />,
  closed: <XCircleIcon className="h-5 w-5 text-gray-500" />,
  cancelled: <XCircleIcon className="h-5 w-5 text-red-500" />
};

const stateLabels = {
  new: 'New',
  in_progress: 'In Progress',
  on_hold: 'On Hold',
  resolved: 'Resolved',
  closed: 'Closed',
  cancelled: 'Cancelled'
};

// Filter options for multi-select
const stateOptions = [
  { value: 'new', label: 'New' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const priorityOptions = [
  { value: 'critical_1', label: 'Critical' },
  { value: 'high_2', label: 'High' },
  { value: 'moderate_3', label: 'Moderate' },
  { value: 'low_4', label: 'Low' },
  { value: 'planning_5', label: 'Planning' },
];

const timeRangeOptions = [
  { value: '5', label: 'Last 5 mins' },
  { value: '15', label: 'Last 15 mins' },
  { value: '30', label: 'Last 30 mins' },
  { value: '60', label: 'Last 1 hour' },
  { value: '120', label: 'Last 2 hours' },
  { value: '480', label: 'Last 8 hours' },
  { value: '1440', label: 'Last 24 hours' },
  { value: 'preset:this_week', label: 'This Week' },
  { value: 'preset:last_week', label: 'Last Week' },
  { value: 'preset:this_month', label: 'This Month' },
  { value: 'preset:last_month', label: 'Last Month' },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const INCIDENT_LIST_URL_FILTER_KEYS = [
  'search',
  'number',
  'desc',
  'state',
  'priority',
  'time',
  'customer',
  'engagement',
  'alert',
  'groups',
  'assignedToMe',
  'assignedTo',
  'unassigned',
  'sla',
  'reportedBy',
  'createdBy',
] as const;

function isDefaultIncidentListUrl(sp: URLSearchParams): boolean {
  const page = sp.get('page');
  if (page && page !== '1') return false;
  const ps = sp.get('pageSize');
  if (ps && ps !== '25') return false;
  for (const k of INCIDENT_LIST_URL_FILTER_KEYS) {
    const v = sp.get(k);
    if (v !== null && v !== '') return false;
  }
  return true;
}

function buildIncidentListUrlUpdatesFromSaved(
  savedFilters: FilterPreferences
): Record<string, string | null | undefined> {
  const updates: Record<string, string | null | undefined> = {};
  // Do not hydrate `state` from saved preferences on load — default list should have no state
  // filters selected; only implicit time default (This Month) applies unless the user sets filters.
  if (savedFilters.priority?.length) updates.priority = savedFilters.priority.join(',');
  if (savedFilters.timeRange === '') {
    updates.time = 'all';
  } else if (savedFilters.timeRange && savedFilters.timeRange !== 'preset:this_month') {
    updates.time = savedFilters.timeRange;
  }
  if (savedFilters.customer) updates.customer = savedFilters.customer;
  if (savedFilters.engagement) {
    updates.engagement = savedFilters.engagement;
  }
  if (savedFilters.assignmentGroup) {
    updates.groups = savedFilters.assignmentGroup;
  }
  if (savedFilters.assignedToMe !== undefined && savedFilters.assignedToMe !== null) {
    updates.assignedToMe =
      savedFilters.assignedToMe === true || savedFilters.assignedToMe === 'true' ? 'true' : 'false';
  }
  if (savedFilters.assignedTo) updates.assignedTo = savedFilters.assignedTo;
  if (savedFilters.unassigned) updates.unassigned = savedFilters.unassigned;
  return updates;
}

function serializeIncidentTimeRangeForUrl(timeRange: string): string | null {
  if (!timeRange) return 'all';
  if (timeRange === 'preset:this_month') return null;
  return timeRange;
}

const IncidentList: React.FC = () => {
  // Subscribe to real-time incident events via SSE
  useTicketEvents({ ticketTypes: ['INCIDENT'] });

  const { user } = useAuth();
  const isServiceDeskUser = useIsServiceDeskUser();
  const hasLoggedView = useRef(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  usePersistListFiltersSession('/incidents', LIST_FILTER_SESSION_KEYS.incidents);

  const patchSearchParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, val] of Object.entries(updates)) {
            if (val === null || val === undefined || val === '') {
              next.delete(key);
            } else {
              next.set(key, val);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') ?? '');
  const appliedSearchTerm = searchParams.get('search') ?? '';
  useEffect(() => {
    setSearchTerm(appliedSearchTerm);
  }, [appliedSearchTerm]);

  const page = useMemo(() => {
    const p = parseInt(searchParams.get('page') || '1', 10);
    return Number.isFinite(p) && p >= 1 ? p : 1;
  }, [searchParams]);

  const pageSize = useMemo(() => {
    const ps = parseInt(searchParams.get('pageSize') || '25', 10);
    return PAGE_SIZE_OPTIONS.includes(ps) ? ps : 25;
  }, [searchParams]);

  const filterState = useMemo(
    () => (searchParams.get('state') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const filterPriority = useMemo(
    () => (searchParams.get('priority') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const filterTimeRange = useMemo(() => {
    const t = searchParams.get('time');
    if (t === 'all') return '';
    if (t === null || t === '') return 'preset:this_month';
    return t;
  }, [searchParams]);

  const filterCustomer = searchParams.get('customer') ?? '';
  const filterEngagement = useMemo(
    () => (searchParams.get('engagement') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const appliedAlertNumber = searchParams.get('alert') ?? '';
  const [filterAlertNumber, setFilterAlertNumber] = useState(() => searchParams.get('alert') ?? '');
  useEffect(() => {
    setFilterAlertNumber(appliedAlertNumber);
  }, [appliedAlertNumber]);

  const filterAssignmentGroup = useMemo(
    () => (searchParams.get('groups') || '').split(',').filter(Boolean),
    [searchParams]
  );
  const filterAssignedToMe = searchParams.get('assignedToMe') ?? '';
  const filterAssignedTo = searchParams.get('assignedTo') ?? '';
  const filterUnassigned = searchParams.get('unassigned') ?? '';
  const filterSlaBreached = searchParams.get('sla') === '1';
  const filterNumber = searchParams.get('number') ?? '';
  const filterDescription = searchParams.get('desc') ?? '';
  const filterReportedBy = searchParams.get('reportedBy') ?? '';
  const filterCreatedBy = searchParams.get('createdBy') ?? '';

  const effectiveSearch = appliedSearchTerm || filterNumber || filterDescription;

  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const savedPrefsHydratedRef = useRef(false);

  // Get browser's timezone offset (in minutes from UTC)
  // This is used to ensure date filtering is consistent with user's local timezone
  const timezoneOffset = useMemo(() => new Date().getTimezoneOffset(), []);

  // Toast notification state
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);

  // Filter preferences
  const {
    filters: savedFilters,
    saveFilters,
    isSaving: isFilterSaving,
    isFetched: isFilterPrefsFetched,
  } = useFilterPreferences('INCIDENT');

  // Apply saved preferences to URL once when URL is still default (no explicit filters)
  useEffect(() => {
    if (filtersInitialized) return;
    if (user?.id && !isFilterPrefsFetched) return;

    if (!isDefaultIncidentListUrl(searchParams)) {
      setFiltersInitialized(true);
      return;
    }

    if (savedPrefsHydratedRef.current) {
      setFiltersInitialized(true);
      return;
    }
    savedPrefsHydratedRef.current = true;

    const updates = buildIncidentListUrlUpdatesFromSaved(savedFilters);
    if (Object.keys(updates).length > 0) {
      patchSearchParams(updates);
    }
    setFiltersInitialized(true);
  }, [
    filtersInitialized,
    isFilterPrefsFetched,
    user?.id,
    searchParams,
    savedFilters,
    patchSearchParams,
  ]);

  // Check if current filters differ from saved
  const savedEngagement = savedFilters.engagement
    ? (typeof savedFilters.engagement === 'string' ? savedFilters.engagement.split(',') : savedFilters.engagement)
    : [];
  const savedAssignmentGroup = savedFilters.assignmentGroup
    ? (typeof savedFilters.assignmentGroup === 'string' ? savedFilters.assignmentGroup.split(',') : savedFilters.assignmentGroup)
    : [];
  // Convert saved assignedToMe to string for comparison ('' if not set, 'true' or 'false' if set)
  const savedAssignedToMe = savedFilters.assignedToMe !== undefined && savedFilters.assignedToMe !== null
    ? (savedFilters.assignedToMe === true || savedFilters.assignedToMe === 'true' ? 'true' : 'false')
    : '';
  const hasUnsavedFilterChanges = filtersInitialized && (
    JSON.stringify(filterState.sort()) !== JSON.stringify((savedFilters.state || []).sort()) ||
    JSON.stringify(filterPriority.sort()) !== JSON.stringify((savedFilters.priority || []).sort()) ||
    filterTimeRange !== (savedFilters.timeRange || '') ||
    filterCustomer !== (savedFilters.customer || '') ||
    JSON.stringify([...filterEngagement].sort()) !== JSON.stringify([...savedEngagement].sort()) ||
    JSON.stringify([...filterAssignmentGroup].sort()) !== JSON.stringify([...savedAssignmentGroup].sort()) ||
    filterAssignedToMe !== savedAssignedToMe ||
    filterAssignedTo !== (savedFilters.assignedTo || '') ||
    filterUnassigned !== (savedFilters.unassigned || '')
  );

  const handleSaveFilters = async () => {
    await saveFilters({
      state: filterState,
      priority: filterPriority,
      timeRange: filterTimeRange || undefined,
      customer: filterCustomer || undefined,
      engagement: filterEngagement.length > 0 ? filterEngagement.join(',') : undefined,
      assignmentGroup: filterAssignmentGroup.length > 0 ? filterAssignmentGroup.join(',') : undefined,
      assignedToMe: filterAssignedToMe || undefined,
      assignedTo: filterAssignedTo || undefined,
      unassigned: filterUnassigned || undefined,
    });
  };

  // Build export filters for background export
  const exportFilters = useMemo(() => {
    // Build time range params
    let timeRangeParam = '';
    let dateFrom = '';
    let dateTo = '';
    if (filterTimeRange) {
      if (filterTimeRange.startsWith('custom:')) {
        const [, dates] = filterTimeRange.split('custom:');
        const [from, to] = dates.split(',');
        dateFrom = from;
        dateTo = to;
      } else if (filterTimeRange.startsWith('preset:')) {
        const presetName = filterTimeRange.replace('preset:', '') as DurationPreset;
        const range = getDurationPresetRange(presetName);
        dateFrom = range.from;
        dateTo = range.to;
      } else {
        timeRangeParam = filterTimeRange;
      }
    }

    return {
      ticketType: 'INCIDENT',
      ...(effectiveSearch && { search: effectiveSearch }),
      ...(filterState.length > 0 && { state: filterState.join(',') }),
      ...(filterPriority.length > 0 && { priority: filterPriority.join(',') }),
      ...(timeRangeParam && { timeRange: timeRangeParam }),
      ...(dateFrom && { dateFrom }),
      ...(dateTo && { dateTo }),
      // Include timezone offset for proper date filtering when date filters are present
      ...((dateFrom || dateTo) && { timezoneOffset: String(timezoneOffset) }),
      ...(filterCustomer && { customerId: filterCustomer }),
      ...(filterEngagement.length > 0 && { engagementId: filterEngagement.join(',') }),
      ...(appliedAlertNumber && { alertNumber: appliedAlertNumber }),
      ...(filterAssignmentGroup.length > 0 && { assignmentGroupId: filterAssignmentGroup.join(',') }),
      ...(filterAssignedToMe && { assignedToMe: filterAssignedToMe }),
      ...(filterAssignedTo && { assignedToId: filterAssignedTo }),
      ...(filterUnassigned && { unassigned: filterUnassigned }),
      ...(filterSlaBreached && { slaBreached: 'true' }),
      ...(filterReportedBy && { callerId: filterReportedBy }),
      ...(filterCreatedBy && { createdById: filterCreatedBy }),
    };
  }, [effectiveSearch, filterState, filterPriority, filterTimeRange, filterCustomer, filterEngagement, appliedAlertNumber, filterAssignmentGroup, filterAssignedToMe, filterAssignedTo, filterUnassigned, filterSlaBreached, filterReportedBy, filterCreatedBy, timezoneOffset]);

  // Multi-select state
  const [selectedIncidents, setSelectedIncidents] = useState<string[]>([]);
  const [bulkCreateModalOpen, setBulkCreateModalOpen] = useState(false);
  const [bulkValidationData, setBulkValidationData] = useState<any>(null);
  const [validationErrorModalOpen, setValidationErrorModalOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{ message: string; errors: string[] }>({ message: '', errors: [] });

  // Email modal state
  const [emailModalState, setEmailModalState] = useState({
    isOpen: false,
    ticketId: '',
    ticketNumber: '',
    shortDescription: ''
  });

  const openEmailModal = (incident: Incident) => {
    setEmailModalState({
      isOpen: true,
      ticketId: incident.id,
      ticketNumber: incident.number,
      shortDescription: incident.shortDescription
    });
  };

  const queryClient = useQueryClient();

  // Survey confirmation modal state
  const [surveyConfirmModal, setSurveyConfirmModal] = useState<{
    isOpen: boolean;
    incidentId: string | null;
  }>({
    isOpen: false,
    incidentId: null
  });

  // Survey result modal state
  const [surveyResultModal, setSurveyResultModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'error';
    message: string;
  }>({
    isOpen: false,
    type: 'success',
    message: ''
  });

  // Send survey mutation
  const sendSurveyMutation = useMutation({
    mutationFn: async (caseId: string) => {
      const response = await api.post(`/cases/${caseId}/send-survey`);
      return response.data;
    },
    onSuccess: async (data) => {
      if (!data?.success) {
        setSurveyResultModal({
          isOpen: true,
          type: 'error',
          message: (data as any)?.message || 'Failed to send survey email'
        });
        return;
      }
      // Show success message
      setSurveyResultModal({
        isOpen: true,
        type: 'success',
        message: 'Survey has been sent to the customer.'
      });
      // Refetch incidents to update survey status - invalidate and refetch all incident-related queries
      await queryClient.invalidateQueries({ queryKey: ['incidents'] });
      await queryClient.invalidateQueries({ queryKey: ['incident-stats'] });
      // Invalidate surveys cache so Survey List shows the new survey when navigated to
      await queryClient.invalidateQueries({ queryKey: ['surveys'] });
      await queryClient.invalidateQueries({ queryKey: ['survey-stats'] });
      // Force immediate refetch to update the UI
      await queryClient.refetchQueries({ queryKey: ['incidents'] });
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Failed to send survey';
      setSurveyResultModal({
        isOpen: true,
        type: 'error',
        message: errorMessage
      });
    }
  });

  const sendSurveyEmail = async (incidentId: string) => {
    sendSurveyMutation.mutate(incidentId);
  };

  const handleSendSurvey = async (incident: Incident) => {
    // Priority-based behavior (reuse existing ticket-closure logic)
    const priority = incident.priority;
    
    // Critical or High priority: Automatically send survey
    if (priority === 'critical_1' || priority === 'high_2') {
      sendSurveyEmail(incident.id);
    } 
    // Moderate or Low priority: Show confirmation popup
    else if (priority === 'moderate_3' || priority === 'low_4') {
      setSurveyConfirmModal({ isOpen: true, incidentId: incident.id });
    } else {
      // For other priorities or unknown, default to sending
      sendSurveyEmail(incident.id);
    }
  };

  const handleSurveyConfirmYes = () => {
    if (surveyConfirmModal.incidentId) {
      setSurveyConfirmModal({ isOpen: false, incidentId: null });
      sendSurveyEmail(surveyConfirmModal.incidentId);
    }
  };

  const handleSurveyConfirmNo = () => {
    setSurveyConfirmModal({ isOpen: false, incidentId: null });
  };

  // List layout settings
  const [showLayoutSettings, setShowLayoutSettings] = useState(false);
  const {
    columns: layoutColumns,
    visibleColumns,
    saveColumns,
    resetToDefault,
    isSaving: isLayoutSaving,
    isResetting: isLayoutResetting,
  } = useListLayoutPreferences('INCIDENT');

  // Build columns array from visible columns for export.
  // Replace single "SLA Breached" with separate Response/Ongoing/Resolution SLA Status columns in CSV.
  const exportColumns = useMemo(() => {
    const cols: { key: string; header: string }[] = [];
    for (const col of visibleColumns) {
      if (col.key === 'slaBreached') {
        cols.push({ key: 'responseSlaStatus', header: 'Response SLA Status' });
        cols.push({ key: 'ongoingSlaStatus', header: 'Ongoing SLA Status' });
        cols.push({ key: 'resolutionSlaStatus', header: 'Resolution SLA Status' });
      } else if (col.key === 'customer') {
        cols.push({ key: col.key, header: col.label || col.key });
        cols.push({ key: 'customerType', header: 'Customer Type' });
      } else {
        cols.push({ key: col.key, header: col.label || col.key });
      }
    }
    return cols;
  }, [visibleColumns]);

  // Fetch customers for dropdown
  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const response = await api.get('/customers');
      return response.data;
    }
  });

  // Fetch user's assignment groups for dropdown
  const { data: myGroupsData } = useQuery({
    queryKey: ['my-assignment-groups'],
    queryFn: async () => {
      const response = await api.get('/groups/my-assignment-groups');
      return response.data;
    }
  });

  // Fetch users for "Assigned To" dropdown
  const { data: usersData } = useQuery({
    queryKey: ['users-for-filter'],
    queryFn: async () => {
      const response = await api.get('/users?limit=500&class=user');
      return response.data;
    }
  });

  const { data: reportedByUsersData } = useQuery({
    queryKey: ['reported-by-filter-options', filterCustomer],
    queryFn: async () => {
      const contactParams = new URLSearchParams();
      if (filterCustomer) contactParams.set('customerId', filterCustomer);

      const [contactsRes, usersRes] = await Promise.all([
        api.get(`/contacts/dropdown${contactParams.toString() ? `?${contactParams}` : ''}`),
        api.get('/users/dropdown'),
      ]);

      const options: { value: string; label: string }[] = [];
      const seen = new Set<string>();

      for (const contact of contactsRes.data?.contacts ?? []) {
        if (!contact.id || seen.has(contact.id)) continue;
        seen.add(contact.id);
        options.push({
          value: contact.id,
          label: contact.name || contact.email || contact.id,
        });
      }

      for (const user of usersRes.data?.users ?? []) {
        if (!user.id || seen.has(user.id)) continue;
        seen.add(user.id);
        options.push({
          value: user.id,
          label: user.name || user.email || user.id,
        });
      }

      options.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
      return options;
    },
  });

  // Format created date function
  const formatCreatedDate = (date: string) => {
    const dateObj = new Date(date);

    // Format as "12/3/2025" on first line, "5:26:49 AM" on second line
    const month = dateObj.getMonth() + 1; // getMonth() returns 0-11
    const day = dateObj.getDate();
    const year = dateObj.getFullYear();

    let hours = dateObj.getHours();
    const minutes = dateObj.getMinutes();
    const seconds = dateObj.getSeconds();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'

    const minutesStr = minutes < 10 ? `0${minutes}` : minutes;
    const secondsStr = seconds < 10 ? `0${seconds}` : seconds;

    return {
      date: `${month}/${day}/${year}`,
      time: `${hours}:${minutesStr}:${secondsStr} ${ampm}`
    };
  };

  // Fetch engagements for dropdown (filtered by customer)
  const { data: engagementsData } = useQuery({
    queryKey: ['engagements', filterCustomer],
    queryFn: async () => {
      if (!filterCustomer) {
        return { engagements: [] };
      }
      const response = await api.get(`/engagements/dropdown?customerId=${filterCustomer}`);
      return response.data;
    },
    enabled: !!filterCustomer
  });

  // Build filter sections for FilterDropdown
  const filterSections: FilterSection[] = useMemo(() => [
    {
      id: 'state',
      label: 'State',
      type: 'multi-select',
      options: stateOptions,
      placeholder: 'All States',
    },
    {
      id: 'priority',
      label: 'Priority',
      type: 'multi-select',
      options: priorityOptions,
      placeholder: 'All Priorities',
    },
    {
      id: 'timeRange',
      label: 'Time Range',
      type: 'time-range',
      options: timeRangeOptions,
      placeholder: 'All Time',
    },
    {
      id: 'customer',
      label: 'Customer',
      type: 'searchable-select',
      options: customersData?.customers?.map((c: any) => ({
        value: c.id,
        label: c.accountName || c.name,
      })) || [],
      placeholder: 'All Customers',
    },
    {
      id: 'engagement',
      label: 'Engagement',
      type: 'searchable-multi-select',
      options: engagementsData?.engagements?.map((e: any) => ({
        value: e.id,
        label: e.name,
      })) || [],
      placeholder: 'All Engagements',
      disabled: !filterCustomer,
      disabledMessage: 'Select a customer first',
    },
    {
      id: 'alertNumber',
      label: 'Alert Number',
      type: 'text',
      placeholder: 'e.g., ALT001, ALT002',
      helpText: 'Enter single or multiple alert numbers separated by commas',
    },
    {
      id: 'assignmentGroup',
      label: 'Assignment Group',
      type: 'searchable-multi-select',
      options: myGroupsData?.map((g: any) => ({
        value: g.id,
        label: g.name,
      })) || [],
      placeholder: 'All My Groups',
    },
    {
      id: 'assignedToMe',
      label: 'Assigned To Me',
      type: 'single-select',
      options: [
        { value: 'true', label: 'Yes' },
        { value: 'false', label: 'No' },
      ],
      placeholder: 'All Assignments',
    },
    {
      id: 'assignedTo',
      label: 'Assigned To',
      type: 'searchable-select',
      options: usersData?.users?.map((u: any) => ({
        value: u.id,
        label: `${u.firstName} ${u.lastName}`,
      })) || [],
      placeholder: 'All Users',
    },
    {
      id: 'unassigned',
      label: 'Show Unassigned',
      type: 'single-select',
      options: [
        { value: 'true', label: 'Yes' },
      ],
      placeholder: 'All',
    },
  ], [customersData, engagementsData, myGroupsData, usersData, filterCustomer]);

  const customerOptions = useMemo(
    () =>
      customersData?.customers?.map((c: { id: string; accountShortName?: string; accountName?: string; name?: string }) => ({
        value: c.id,
        label: c.accountShortName || c.accountName || c.name || c.id,
      })) ?? [],
    [customersData]
  );

  const engagementOptions = useMemo(
    () =>
      engagementsData?.engagements?.map((e: { id: string; name: string }) => ({
        value: e.id,
        label: e.name,
      })) ?? [],
    [engagementsData]
  );

  const assignmentGroupOptions = useMemo(
    () =>
      myGroupsData?.map((g: { id: string; name: string }) => ({
        value: g.id,
        label: g.name,
      })) ?? [],
    [myGroupsData]
  );

  const assignedToOptions = useMemo(() => {
    const userOptions =
      usersData?.users?.map((u: { id: string; firstName?: string; lastName?: string; email?: string }) => ({
        value: u.id,
        label: [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || u.id,
      })) ?? [];
    return [{ value: '__unassigned__', label: 'Unassigned' }, ...userOptions];
  }, [usersData]);

  const mapUserToFilterOption = (u: {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    name?: string;
    displayName?: string;
  }) => ({
    value: u.id,
    label:
      u.displayName ||
      [u.firstName, u.lastName].filter(Boolean).join(' ') ||
      u.name ||
      u.email ||
      u.id,
  });

  const reportedByUserOptions = useMemo(
    () => reportedByUsersData ?? [],
    [reportedByUsersData]
  );

  const createdByUserOptions = useMemo(
    () => usersData?.users?.map(mapUserToFilterOption) ?? [],
    [usersData]
  );

  const handleAssignedToColumnFilter = useCallback(
    (value: string | string[]) => {
      const v = typeof value === 'string' ? value : '';
      if (v === '__unassigned__') {
        patchSearchParams({ assignedTo: null, unassigned: 'true', page: '1' });
      } else if (v) {
        patchSearchParams({ assignedTo: v, unassigned: null, page: '1' });
      } else {
        patchSearchParams({ assignedTo: null, unassigned: null, page: '1' });
      }
    },
    [patchSearchParams]
  );

  const assignedToColumnValue = filterUnassigned === 'true' ? '__unassigned__' : filterAssignedTo;

  const createdTimeRangeOptions = useMemo(
    () => [...timeRangeOptions, { value: 'all', label: 'All Time' }],
    []
  );

  const renderColumnHeader = useCallback(
    (columnKey: string, columnLabel: string) => {
      switch (columnKey) {
        case 'number':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterNumber}
              placeholder="Filter by number (comma or space separated)..."
              onChange={(v) => {
                patchSearchParams({ number: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'shortDescription':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterDescription}
              placeholder="Filter by description..."
              onChange={(v) => {
                patchSearchParams({ desc: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'reportedBy':
        case 'caller':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={reportedByUserOptions}
              value={filterReportedBy}
              placeholder="All Users"
              onChange={(v) => {
                patchSearchParams({ reportedBy: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'createdBy':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={createdByUserOptions}
              value={filterCreatedBy}
              placeholder="All Users"
              onChange={(v) => {
                patchSearchParams({ createdBy: typeof v === 'string' && v ? v : null, page: '1' });
              }}
            />
          );
        case 'customer':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={customerOptions}
              value={filterCustomer}
              placeholder="All Customers"
              onChange={(v) => {
                patchSearchParams({
                  customer: typeof v === 'string' && v ? v : null,
                  engagement: null,
                  page: '1',
                });
              }}
            />
          );
        case 'engagement':
          if (!filterCustomer) return columnLabel;
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={engagementOptions}
              value={filterEngagement}
              placeholder="All Engagements"
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : [];
                patchSearchParams({ engagement: arr.length > 0 ? arr.join(',') : null, page: '1' });
              }}
            />
          );
        case 'priority':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={priorityOptions}
              value={filterPriority}
              placeholder="All Priorities"
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : [];
                patchSearchParams({ priority: arr.length > 0 ? arr.join(',') : null, page: '1' });
              }}
            />
          );
        case 'state':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={stateOptions}
              value={filterState}
              placeholder="All States"
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : [];
                patchSearchParams({ state: arr.length > 0 ? arr.join(',') : null, page: '1' });
              }}
            />
          );
        case 'assignedTo':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="searchable-select"
              options={assignedToOptions}
              value={assignedToColumnValue}
              placeholder="All Users"
              onChange={handleAssignedToColumnFilter}
            />
          );
        case 'assignmentGroup':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="multi-select"
              options={assignmentGroupOptions}
              value={filterAssignmentGroup}
              placeholder="All My Groups"
              onChange={(v) => {
                const arr = Array.isArray(v) ? v : [];
                patchSearchParams({ groups: arr.length > 0 ? arr.join(',') : null, page: '1' });
              }}
            />
          );
        case 'createdAt':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="time-range"
              options={createdTimeRangeOptions}
              value={filterTimeRange || 'all'}
              placeholder="All Time"
              active={searchParams.has('time')}
              onChange={(v) => {
                const val = typeof v === 'string' ? v : '';
                const timeParam = serializeIncidentTimeRangeForUrl(val === 'all' ? '' : val);
                patchSearchParams({ time: timeParam, page: '1' });
              }}
            />
          );
        case 'slaBreached':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={[{ value: 'true', label: 'SLA Breached' }]}
              value={filterSlaBreached ? 'true' : ''}
              placeholder="All"
              onChange={(v) => {
                patchSearchParams({ sla: v === 'true' ? '1' : null, page: '1' });
              }}
            />
          );
        default:
          return columnLabel;
      }
    },
    [
      filterNumber,
      filterDescription,
      filterReportedBy,
      filterCreatedBy,
      customerOptions,
      filterCustomer,
      engagementOptions,
      filterEngagement,
      filterPriority,
      filterState,
      assignedToOptions,
      assignedToColumnValue,
      handleAssignedToColumnFilter,
      reportedByUserOptions,
      createdByUserOptions,
      assignmentGroupOptions,
      filterAssignmentGroup,
      createdTimeRangeOptions,
      filterTimeRange,
      searchParams,
      filterSlaBreached,
      patchSearchParams,
    ]
  );

  // Build active filter chips
  const activeFilterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];

    if (appliedSearchTerm) {
      chips.push({
        id: 'search',
        label: 'Search',
        value: `"${appliedSearchTerm}"`,
        color: 'blue',
      });
    }

    if (filterNumber) {
      chips.push({
        id: 'number',
        label: 'Number',
        value: `"${filterNumber}"`,
        color: 'blue',
      });
    }

    if (filterDescription) {
      chips.push({
        id: 'desc',
        label: 'Description',
        value: `"${filterDescription}"`,
        color: 'blue',
      });
    }

    if (filterReportedBy) {
      const option = reportedByUserOptions.find((o) => o.value === filterReportedBy);
      chips.push({
        id: 'reportedBy',
        label: 'Reported By',
        value: option?.label || filterReportedBy,
        color: 'teal',
      });
    }

    if (filterCreatedBy) {
      const user = usersData?.users?.find((u: { id: string }) => u.id === filterCreatedBy);
      chips.push({
        id: 'createdBy',
        label: 'Created By',
        value: user
          ? [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || filterCreatedBy
          : filterCreatedBy,
        color: 'teal',
      });
    }

    if (filterState.length > 0) {
      chips.push({
        id: 'state',
        label: 'State',
        value: filterState.map(s => stateLabels[s as keyof typeof stateLabels] || s).join(', '),
        color: 'purple',
      });
    }

    if (filterPriority.length > 0) {
      chips.push({
        id: 'priority',
        label: 'Priority',
        value: filterPriority.map(p => priorityLabels[p] || p).join(', '),
        color: 'orange',
      });
    }

    if (filterTimeRange) {
      let timeLabel = '';
      if (filterTimeRange.startsWith('custom:')) {
        const [, dates] = filterTimeRange.split('custom:');
        const [from, to] = dates.split(',');
        timeLabel = `${from} to ${to}`;
      } else {
        const timeOption = timeRangeOptions.find(t => t.value === filterTimeRange);
        timeLabel = timeOption?.label || filterTimeRange;
      }
      chips.push({
        id: 'timeRange',
        label: 'Time',
        value: timeLabel,
        color: 'orange',
      });
    }

    if (filterCustomer) {
      const customer = customersData?.customers?.find((c: any) => c.id === filterCustomer);
      chips.push({
        id: 'customer',
        label: 'Customer',
        value: customer?.accountName || customer?.name || 'Unknown',
        color: 'teal',
      });
    }

    if (filterEngagement.length > 0) {
      const engagementNames = filterEngagement.map(engId => {
        const engagement = engagementsData?.engagements?.find((e: any) => e.id === engId);
        return engagement?.name || 'Unknown';
      });
      chips.push({
        id: 'engagement',
        label: 'Engagement',
        value: engagementNames.join(', '),
        color: 'cyan',
      });
    }

    if (appliedAlertNumber) {
      chips.push({
        id: 'alertNumber',
        label: 'Alert Number',
        value: appliedAlertNumber,
        color: 'red',
      });
    }

    if (filterAssignmentGroup.length > 0) {
      const groupNames = filterAssignmentGroup.map(groupId => {
        const group = myGroupsData?.find((g: any) => g.id === groupId);
        return group?.name || 'Unknown';
      });
      chips.push({
        id: 'assignmentGroup',
        label: 'Assignment Group',
        value: groupNames.join(', '),
        color: 'purple',
      });
    }

    if (filterAssignedToMe) {
      chips.push({
        id: 'assignedToMe',
        label: 'Assigned To Me',
        value: filterAssignedToMe === 'true' ? 'Yes' : 'No',
        color: filterAssignedToMe === 'true' ? 'green' : 'gray',
      });
    }

    if (filterAssignedTo) {
      const assignedUser = usersData?.users?.find((u: any) => u.id === filterAssignedTo);
      chips.push({
        id: 'assignedTo',
        label: 'Assigned To',
        value: assignedUser ? `${assignedUser.firstName} ${assignedUser.lastName}` : 'Unknown',
        color: 'teal',
      });
    }

    if (filterUnassigned === 'true') {
      chips.push({
        id: 'unassigned',
        label: 'Show Unassigned',
        value: 'Yes',
        color: 'orange',
      });
    }

    if (filterSlaBreached) {
      chips.push({
        id: 'sla',
        label: 'SLA Breached',
        value: 'Yes',
        color: 'red',
      });
    }

    return chips;
  }, [appliedSearchTerm, filterNumber, filterDescription, filterReportedBy, filterCreatedBy, filterState, filterPriority, filterTimeRange, filterCustomer, filterEngagement, appliedAlertNumber, filterAssignmentGroup, filterAssignedToMe, filterAssignedTo, filterUnassigned, filterSlaBreached, customersData, engagementsData, myGroupsData, usersData, reportedByUserOptions]);

  // Handle filter dropdown changes
  const handleFilterChange = (sectionId: string, value: string | string[]) => {
    switch (sectionId) {
      case 'state': {
        const arr = value as string[];
        patchSearchParams({
          state: arr.length > 0 ? arr.join(',') : null,
          page: '1',
        });
        break;
      }
      case 'priority': {
        const arr = value as string[];
        patchSearchParams({
          priority: arr.length > 0 ? arr.join(',') : null,
          page: '1',
        });
        break;
      }
      case 'timeRange': {
        const v = value as string;
        const timeParam = serializeIncidentTimeRangeForUrl(v);
        patchSearchParams({
          time: timeParam,
          page: '1',
        });
        break;
      }
      case 'customer':
        patchSearchParams({
          customer: (value as string) || null,
          engagement: null,
          page: '1',
        });
        break;
      case 'engagement': {
        const arr = value as string[];
        patchSearchParams({
          engagement: arr.length > 0 ? arr.join(',') : null,
          page: '1',
        });
        break;
      }
      case 'alertNumber':
        setFilterAlertNumber(value as string);
        break;
      case 'assignmentGroup': {
        const arr = value as string[];
        patchSearchParams({
          groups: arr.length > 0 ? arr.join(',') : null,
          page: '1',
        });
        break;
      }
      case 'assignedToMe':
        patchSearchParams({
          assignedToMe:
            value === 'true' || value === 'false' ? (value as string) : null,
          page: '1',
        });
        break;
      case 'assignedTo':
        patchSearchParams({
          assignedTo: (value as string) || null,
          page: '1',
        });
        break;
      case 'unassigned':
        patchSearchParams({
          unassigned: value === 'true' ? 'true' : null,
          page: '1',
        });
        break;
    }
  };

  // Handle applying alert number filter
  const handleApplyFilters = () => {
    if (filterAlertNumber !== appliedAlertNumber) {
      patchSearchParams({
        alert: filterAlertNumber.trim() || null,
        page: '1',
      });
    }
  };

  // Handle removing a single filter chip
  const handleRemoveFilterChip = (chipId: string) => {
    switch (chipId) {
      case 'search':
        setSearchTerm('');
        patchSearchParams({ search: null, page: '1' });
        break;
      case 'number':
        patchSearchParams({ number: null, page: '1' });
        break;
      case 'desc':
        patchSearchParams({ desc: null, page: '1' });
        break;
      case 'reportedBy':
        patchSearchParams({ reportedBy: null, page: '1' });
        break;
      case 'createdBy':
        patchSearchParams({ createdBy: null, page: '1' });
        break;
      case 'state':
        patchSearchParams({ state: null, page: '1' });
        break;
      case 'priority':
        patchSearchParams({ priority: null, page: '1' });
        break;
      case 'timeRange':
        patchSearchParams({ time: 'all', page: '1' });
        break;
      case 'customer':
        patchSearchParams({ customer: null, engagement: null, page: '1' });
        break;
      case 'engagement':
        patchSearchParams({ engagement: null, page: '1' });
        break;
      case 'alertNumber':
        setFilterAlertNumber('');
        patchSearchParams({ alert: null, page: '1' });
        break;
      case 'assignmentGroup':
        patchSearchParams({ groups: null, page: '1' });
        break;
      case 'assignedToMe':
        patchSearchParams({ assignedToMe: null, page: '1' });
        break;
      case 'assignedTo':
        patchSearchParams({ assignedTo: null, page: '1' });
        break;
      case 'unassigned':
        patchSearchParams({ unassigned: null, page: '1' });
        break;
      case 'sla':
        patchSearchParams({ sla: null, page: '1' });
        break;
    }
  };

  // Handle clearing all filters
  const handleClearAllFilters = () => {
    setSearchTerm('');
    setFilterAlertNumber('');
    patchSearchParams({
      search: null,
      number: null,
      desc: null,
      reportedBy: null,
      createdBy: null,
      state: null,
      priority: null,
      time: 'all',
      customer: null,
      engagement: null,
      alert: null,
      groups: null,
      assignedToMe: null,
      assignedTo: null,
      unassigned: null,
      sla: null,
      page: '1',
    });
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['incidents', page, pageSize, effectiveSearch, filterState, filterPriority, filterTimeRange, filterCustomer, filterEngagement, appliedAlertNumber, filterAssignmentGroup, filterAssignedToMe, filterAssignedTo, filterUnassigned, filterSlaBreached, filterReportedBy, filterCreatedBy, timezoneOffset],
    queryFn: async () => {
      // Build time range params
      let timeRangeParam = '';
      let dateFrom = '';
      let dateTo = '';
      if (filterTimeRange) {
        if (filterTimeRange.startsWith('custom:')) {
          const [, dates] = filterTimeRange.split('custom:');
          const [from, to] = dates.split(',');
          dateFrom = from;
          dateTo = to;
        } else if (filterTimeRange.startsWith('preset:')) {
          const presetName = filterTimeRange.replace('preset:', '') as DurationPreset;
          const range = getDurationPresetRange(presetName);
          dateFrom = range.from;
          dateTo = range.to;
        } else {
          timeRangeParam = filterTimeRange;
        }
      }

      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
        ticketType: 'INCIDENT',
        ...(effectiveSearch && { search: effectiveSearch }),
        ...(filterState.length > 0 && { state: filterState.join(',') }),
        ...(filterPriority.length > 0 && { priority: filterPriority.join(',') }),
        ...(timeRangeParam && { timeRange: timeRangeParam }),
        ...(dateFrom && { dateFrom }),
        ...(dateTo && { dateTo }),
        // Include timezone offset for proper date filtering when date filters are present
        ...((dateFrom || dateTo) && { timezoneOffset: String(timezoneOffset) }),
        ...(filterCustomer && { customerId: filterCustomer }),
        ...(filterEngagement.length > 0 && { engagementId: filterEngagement.join(',') }),
        ...(appliedAlertNumber && { alertNumber: appliedAlertNumber }),
        ...(filterAssignmentGroup.length > 0 && { assignmentGroupId: filterAssignmentGroup.join(',') }),
        ...(filterAssignedToMe && { assignedToMe: filterAssignedToMe }),
        ...(filterAssignedTo && { assignedToId: filterAssignedTo }),
        ...(filterUnassigned && { unassigned: filterUnassigned }),
        ...(filterSlaBreached && { slaBreached: 'true' }),
        ...(filterReportedBy && { callerId: filterReportedBy }),
        ...(filterCreatedBy && { createdById: filterCreatedBy })
      });

      const response = await api.get(`/cases?${params}`);
      return response.data;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: 30000 // 30 seconds
  });

  // Log "Incident List Viewed" when data is loaded successfully
  useEffect(() => {
    if (data && user && !hasLoggedView.current && !isLoading) {
      hasLoggedView.current = true;
      trackIncidentOperation(
        'Incident List',
        user.email || user.firstName || 'Unknown',
        {
          action: 'Incident List Viewed',
        },
        {
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
          roleType: user.roleType,
          roleLevel: user.roleLevel,
          userClass: user.class,
          customerId: user.customerId,
          customerName: user.customerName,
          engagementId: user.engagementId,
          engagementName: user.engagementName,
          isServiceDeskUser: isServiceDeskUser,
        }
      );
    }
  }, [data, user, isLoading, isServiceDeskUser]);

  // Fetch incident stats for the summary bar (with same filters as list)
  const { data: statsData } = useQuery({
    queryKey: ['incident-stats', effectiveSearch, filterState, filterPriority, filterTimeRange, filterCustomer, filterEngagement, appliedAlertNumber, filterAssignmentGroup, filterAssignedToMe, filterAssignedTo, filterUnassigned, filterReportedBy, filterCreatedBy, timezoneOffset],
    queryFn: async () => {
      // Build time range params (same as list query)
      let timeRangeParam = '';
      let dateFrom = '';
      let dateTo = '';
      if (filterTimeRange) {
        if (filterTimeRange.startsWith('custom:')) {
          const [, dates] = filterTimeRange.split('custom:');
          const [from, to] = dates.split(',');
          dateFrom = from;
          dateTo = to;
        } else if (filterTimeRange.startsWith('preset:')) {
          const presetName = filterTimeRange.replace('preset:', '') as DurationPreset;
          const range = getDurationPresetRange(presetName);
          dateFrom = range.from;
          dateTo = range.to;
        } else {
          timeRangeParam = filterTimeRange;
        }
      }

      const params = new URLSearchParams({
        ticketType: 'INCIDENT',
        ...(effectiveSearch && { search: effectiveSearch }),
        ...(filterState.length > 0 && { state: filterState.join(',') }),
        ...(filterPriority.length > 0 && { priority: filterPriority.join(',') }),
        ...(timeRangeParam && { timeRange: timeRangeParam }),
        ...(dateFrom && { dateFrom }),
        ...(dateTo && { dateTo }),
        // Include timezone offset for proper date filtering when date filters are present
        ...((dateFrom || dateTo) && { timezoneOffset: String(timezoneOffset) }),
        ...(filterCustomer && { customerId: filterCustomer }),
        ...(filterEngagement.length > 0 && { engagementId: filterEngagement.join(',') }),
        ...(appliedAlertNumber && { alertNumber: appliedAlertNumber }),
        ...(filterAssignmentGroup.length > 0 && { assignmentGroupId: filterAssignmentGroup.join(',') }),
        ...(filterAssignedToMe && { assignedToMe: filterAssignedToMe }),
        ...(filterAssignedTo && { assignedToId: filterAssignedTo }),
        ...(filterUnassigned && { unassigned: filterUnassigned }),
        ...(filterReportedBy && { callerId: filterReportedBy }),
        ...(filterCreatedBy && { createdById: filterCreatedBy })
      });

      const response = await api.get(`/cases/stats?${params}`);
      return response.data;
    },
    refetchOnWindowFocus: false,
    staleTime: 30000 // 30 seconds - match list query
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    patchSearchParams({
      search: searchTerm.trim() || null,
      page: '1',
    });
  };

  // Multi-select handlers
  const handleSelectAll = () => {
    // Filter out incidents that are closed, cancelled, or resolved
    const selectableIncidents = data?.cases?.filter((incident: Incident) =>
      incident.state !== 'closed' && incident.state !== 'cancelled' && incident.state !== 'resolved'
    ) || [];

    const selectableIds = selectableIncidents.map((incident: Incident) => incident.id);

    if (selectedIncidents.length === selectableIds.length) {
      setSelectedIncidents([]);
    } else {
      setSelectedIncidents(selectableIds);
    }
  };

  const handleSelectIncident = (incidentId: string) => {
    setSelectedIncidents(prev => {
      if (prev.includes(incidentId)) {
        return prev.filter(id => id !== incidentId);
      } else {
        return [...prev, incidentId];
      }
    });
  };

  const handleBulkCreate = async () => {
    if (selectedIncidents.length === 0) {
      return;
    }

    try {
      // Validate selected incidents before opening modal
      const response = await api.post('/bulk/validate-common-fields', {
        caseIds: selectedIncidents
      });

      if (!response.data.valid) {
        // Show validation error modal
        const errorMessage = response.data.message || 'Selected incidents have different values';
        const errors = response.data.errors || [];

        setValidationErrors({ message: errorMessage, errors });
        setValidationErrorModalOpen(true);
        return;
      }

      // If validation passes, store the data and open the modal
      setBulkValidationData(response.data);
      setBulkCreateModalOpen(true);
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Failed to validate selected incidents';
      setValidationErrors({ message: errorMsg, errors: [] });
      setValidationErrorModalOpen(true);
    }
  };

  const handleCloseBulkModal = () => {
    setBulkCreateModalOpen(false);
    setSelectedIncidents([]);
    setBulkValidationData(null);
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">Error loading incidents. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 flex flex-col h-full min-h-0 overflow-hidden">
      {/* Compact Header Bar */}
      <div className="bg-white shadow rounded-lg mb-2 flex-shrink-0">
        <div className="px-4 py-2">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
            {/* Left: Title and Filter */}
            <div className="flex items-center gap-4">
              <h1 className="text-lg font-semibold text-gray-900 whitespace-nowrap">Incidents</h1>
              <FilterDropdown
                sections={filterSections}
                values={{
                  state: filterState,
                  priority: filterPriority,
                  timeRange: filterTimeRange,
                  customer: filterCustomer,
                  engagement: filterEngagement,
                  alertNumber: filterAlertNumber,
                  assignmentGroup: filterAssignmentGroup,
                  assignedToMe:
                    filterAssignedToMe === 'true' || filterAssignedToMe === 'false'
                      ? filterAssignedToMe
                      : '',
                  assignedTo: filterAssignedTo,
                  unassigned: filterUnassigned,
                }}
                onChange={handleFilterChange}
                onApply={handleApplyFilters}
              />
            </div>

            {/* Right: Search, New Button, and Settings */}
            <form onSubmit={handleSearch} className="flex items-center gap-2">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search incidents or numbers (comma/space separated)..."
                  className="pl-8 pr-3 py-1.5 w-64 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <button
                type="submit"
                disabled={!isTicketSearchInputValid(searchTerm)}
                className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Search
              </button>
              <Link
                to="/incidents/new"
                className="inline-flex items-center px-3 py-1.5 text-sm border border-transparent rounded-md shadow-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <PlusIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
                New
              </Link>
              <ListLayoutSettingsButton onClick={() => setShowLayoutSettings(true)} />
              <RefreshButton
                queryKeys={['incidents', 'incident-stats']}
                tooltip="Refresh incidents"
                size="md"
                isLoading={isLoading}
              />
            </form>
          </div>

          {/* Active Filters - Inline */}
          {activeFilterChips.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <ActiveFilterChips
                chips={activeFilterChips}
                onRemove={handleRemoveFilterChip}
                onClearAll={handleClearAllFilters}
                onSaveAsDefault={handleSaveFilters}
                isSaving={isFilterSaving}
                hasUnsavedChanges={hasUnsavedFilterChanges}
              />
            </div>
          )}

          {/* Stats Summary Bar */}
          <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between text-sm">
            <div className="flex items-center gap-6">
              <span className="text-gray-600">
                Total: <span className="font-semibold text-gray-900">{statsData?.total?.toLocaleString() ?? data?.total?.toLocaleString() ?? '-'}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                <span className="text-gray-600">Critical:</span>
                <span className="font-semibold text-gray-900">{statsData?.byPriority?.critical_1 ?? '-'}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                <span className="text-gray-600">High:</span>
                <span className="font-semibold text-gray-900">{statsData?.byPriority?.high_2 ?? '-'}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                <span className="text-gray-600">In Progress:</span>
                <span className="font-semibold text-gray-900">{statsData?.byState?.in_progress ?? '-'}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                <span className="text-gray-600">Unassigned:</span>
                <span className="font-semibold text-gray-900">{statsData?.unassigned ?? '-'}</span>
              </span>
            </div>
            <div className="flex items-center gap-3">
              {/* Export CSV Button */}
              <GenericExportButton
                config={ExportConfigs.INCIDENTS}
                filters={exportFilters}
                columns={exportColumns}
                totalRows={data?.total}
                disabled={!data?.cases || data.cases.length === 0}
                onExportStart={() => {
                  // Log export button click
                  if (user) {
                    trackIncidentOperation(
                      'Incident List',
                      user.email || user.firstName || 'Unknown',
                      {
                        action: 'Export button clicked  – Incident List',
                      },
                      {
                      firstName: user.firstName,
                      lastName: user.lastName,
                        role: user.role,
                        roleType: user.roleType,
                        roleLevel: user.roleLevel,
                        userClass: user.class,
                        customerId: user.customerId,
                        customerName: user.customerName,
                        engagementId: user.engagementId,
                        engagementName: user.engagementName,
                        isServiceDeskUser: isServiceDeskUser,
                      }
                    );
                  }
                }}
              /> 
              {(statsData?.slaBreached ?? 0) > 0 && (
                <button
                  onClick={() => {
                    patchSearchParams({
                      sla: filterSlaBreached ? null : '1',
                      page: '1',
                    });
                  }}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors ${
                    filterSlaBreached
                      ? 'bg-red-100 text-red-700 ring-2 ring-red-500'
                      : 'text-red-600 hover:bg-red-50'
                  }`}
                  title={filterSlaBreached ? 'Click to show all incidents' : 'Click to filter by SLA breached'}
                >
                  <ExclamationTriangleIcon className="h-4 w-4" />
                  <span>SLA Breached:</span>
                  <span className="font-semibold">{statsData?.slaBreached}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Action Bar - Compact */}
      {selectedIncidents.length > 0 && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-2 mb-2 flex-shrink-0">
          <div className="flex items-center justify-between">
            <span className="text-sm text-indigo-700">
              <span className="font-medium">{selectedIncidents.length}</span> selected
            </span>
            <button
              onClick={handleBulkCreate}
              className="inline-flex items-center px-3 py-1 text-sm border border-transparent rounded-md font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <DocumentPlusIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
              Create Related
            </button>
          </div>
        </div>
      )}

      {/* Incidents Table - Takes remaining space */}
      <div className="bg-white shadow rounded-lg overflow-hidden flex-1 min-h-0 flex flex-col">
        <div className="overflow-scroll flex-1" style={{ scrollbarGutter: 'stable' }}>
          <table className="min-w-full divide-y divide-gray-200" style={{ minWidth: '1200px' }}>
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                <input
                  type="checkbox"
                  checked={(() => {
                    const selectableIncidents = data?.cases?.filter((incident: Incident) =>
                      incident.state !== 'closed' && incident.state !== 'cancelled' && incident.state !== 'resolved'
                    ) || [];
                    return selectableIncidents.length > 0 && selectedIncidents.length === selectableIncidents.length;
                  })()}
                  onChange={handleSelectAll}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
              </th>
              {visibleColumns.map((col) => (
                <th key={col.key} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  {renderColumnHeader(col.key, col.label)}
                </th>
              ))}
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                Survey
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data?.cases && data.cases.length > 0 ? data.cases.map((incident: Incident) => {
              // Column renderer function
              const renderColumn = (columnKey: string) => {
                switch (columnKey) {
                  case 'number':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/incidents/${incident.id || incident._id}`}
                            state={{ incidentsListSearch: location.search }}
                            className="text-indigo-600 hover:text-indigo-900 hover:underline"
                            title="View incident details"
                          >
                            {incident.number}
                          </Link>
                          {incident.relatedRecordsCount !== undefined && incident.relatedRecordsCount > 0 && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 border border-indigo-200"
                              title={`${incident.relatedRecordsCount} related record${incident.relatedRecordsCount > 1 ? 's' : ''}`}
                            >
                              {incident.relatedRecordsCount}
                            </span>
                          )}
                        </div>
                      </td>
                    );
                  case 'shortDescription':
                    return (
                      <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                        <div className="max-w-xs truncate">{stripHtml(incident.shortDescription)}</div>
                      </td>
                    );
                  case 'caller':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {incident.caller && (incident.caller.firstName || incident.caller.email) ? (
                          <>
                            {`${[incident.caller.firstName, incident.caller.lastName].filter(Boolean).join(' ') || incident.caller.email}`}
                          </>
                        ) : (
                          <span className="text-gray-500">No Reported By</span>
                        )}
                      </td>
                    );
                  case 'customer':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {incident.Customer ? (incident.Customer.accountShortName || incident.Customer.accountName) : <span className="text-gray-500">-</span>}
                      </td>
                    );
                  case 'engagement':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {incident.Engagement ? incident.Engagement.name : <span className="text-gray-500">-</span>}
                      </td>
                    );
                  case 'priority':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${priorityColors[incident.priority] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
                          {priorityLabels[incident.priority] || incident.priority}
                        </span>
                      </td>
                    );
                  case 'assignedTo':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {incident.assignedTo && (incident.assignedTo.firstName || incident.assignedTo.email) ? (
                          <>
                            {`${[incident.assignedTo.firstName, incident.assignedTo.lastName].filter(Boolean).join(' ') || incident.assignedTo.email}`}
                          </>
                        ) : (
                          <span className="text-gray-500">Unassigned</span>
                        )}
                      </td>
                    );
                  case 'state':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm">
                        <div className="flex items-center">
                          {stateIcons[incident.state as keyof typeof stateIcons]}
                          <span className="ml-2 text-gray-900">
                            {stateLabels[incident.state as keyof typeof stateLabels]}
                          </span>
                        </div>
                      </td>
                    );
                  case 'assignmentGroup':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {(incident as any).AssignmentGroup?.name || '-'}
                      </td>
                    );
                  case 'impact':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {(incident as any).impact ? (incident as any).impact.replace('_', ' ') : '-'}
                      </td>
                    );
                  case 'urgency':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {(incident as any).urgency ? (incident as any).urgency.replace('_', ' ') : '-'}
                      </td>
                    );
                  case 'category':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {incident.category || '-'}
                      </td>
                    );
                  case 'slaPlan':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {(incident as any).ServicePlan?.name || '-'}
                      </td>
                    );
                  case 'slaBreached':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm">
                        <SlaStatusDisplay slaStatus={(incident as any).slaStatus} />
                      </td>
                    );
                  case 'responseBreachReason':
                    return (
                      <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                        <TruncatedTextWithTooltip text={(incident as any).responseBreachReason} />
                      </td>
                    );
                  case 'ongoingBreachReason':
                    return (
                      <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                        <TruncatedTextWithTooltip text={(incident as any).ongoingBreachReason} />
                      </td>
                    );
                  case 'resolutionBreachReason':
                    return (
                      <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                        <TruncatedTextWithTooltip text={(incident as any).resolutionBreachReason} />
                      </td>
                    );
                  case 'resolvedAt':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        {(incident as any).resolvedAt ? new Date((incident as any).resolvedAt).toLocaleString() : '-'}
                      </td>
                    );
                  case 'resolutionNotes':
                    return (
                      <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                        <div className="max-w-xs truncate">{(incident as any).resolutionNotes ? stripHtml((incident as any).resolutionNotes) : '-'}</div>
                      </td>
                    );
                  case 'closedAt':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        {(incident as any).closedAt ? new Date((incident as any).closedAt).toLocaleString() : '-'}
                      </td>
                    );
                  case 'closureNotes':
                    return (
                      <td key={columnKey} className="px-3 py-2 text-sm text-gray-900">
                        <div className="max-w-xs truncate">{(incident as any).closureNotes ? stripHtml((incident as any).closureNotes) : '-'}</div>
                      </td>
                    );
                  case 'reportedBy':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {incident.caller && (incident.caller.firstName || incident.caller.email) ? (
                          <>
                            {`${[incident.caller.firstName, incident.caller.lastName].filter(Boolean).join(' ') || incident.caller.email}`}
                          </>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>
                    );
                  case 'createdBy':
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                        {incident.createdBy && (incident.createdBy.firstName || incident.createdBy.email) ? (
                          <>
                            {`${[incident.createdBy.firstName, incident.createdBy.lastName].filter(Boolean).join(' ') || incident.createdBy.email}`}
                          </>
                        ) : (
                          <span className="text-gray-500">Unknown</span>
                        )}
                      </td>
                    );
                  case 'createdAt':
                    const createdDate = formatCreatedDate(incident.createdAt);
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        {incident.createdAt ? (
                          <div className="flex flex-col">
                            <span>{createdDate.date}</span>
                            <span className="text-xs">{createdDate.time}</span>
                          </div>
                        ) : '-'}
                      </td>
                    );
                  case 'survey':
                    // Survey column - only show for closed incidents, but not for Planning priority
                    if (incident.ticketType === 'INCIDENT' && 
                        (incident.state === 'closed' || incident.state === 'closed_complete' || incident.state === 'closed_incomplete')) {
                      // Don't show Send Survey button for Planning priority
                      const isPlanningPriority = incident.priority === 'planning_5' || incident.priority === 'planning' || incident.priority === 'PLANNING';
                      
                      return (
                        <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm">
                          {incident.surveySent ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200" title="Survey already sent">
                              Sent
                            </span>
                          ) : incident.surveyEligible && !isPlanningPriority ? (
                            <button
                              onClick={() => handleSendSurvey(incident)}
                              disabled={sendSurveyMutation.isPending}
                              className="text-indigo-600 hover:text-indigo-900 inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Send survey"
                            >
                              <ArrowUturnRightIcon className="h-5 w-5" />
                            </button>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                      );
                    }
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-400">
                        -
                      </td>
                    );
                  default:
                    return (
                      <td key={columnKey} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        -
                      </td>
                    );
                }
              };

              return (
              <tr key={incident.id || incident._id || incident.number} className="hover:bg-gray-50">
                <td className="px-3 py-2 whitespace-nowrap text-sm">
                  <input
                    type="checkbox"
                    checked={selectedIncidents.includes(incident.id)}
                    onChange={() => handleSelectIncident(incident.id)}
                    disabled={incident.state === 'closed' || incident.state === 'cancelled' || incident.state === 'resolved'}
                    className={`h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded ${
                      incident.state === 'closed' || incident.state === 'cancelled' || incident.state === 'resolved' ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  />
                </td>
                {visibleColumns.map((col) => renderColumn(col.key))}
                {/* Survey column */}
                {renderColumn('survey')}
                <td className="px-3 py-2 whitespace-nowrap text-sm">
                  <button
                    onClick={() => openEmailModal(incident)}
                    className="text-blue-600 hover:text-blue-900 inline-flex items-center"
                    title="Email ticket details"
                  >
                    <EnvelopeIcon className="h-5 w-5" />
                  </button>
                </td>
              </tr>
              );
            }) : null}
          </tbody>
        </table>
        </div>

        {data?.cases?.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No incidents found</p>
          </div>
        )}
      </div>

      {/* Pagination Controls - Always visible */}
      <div className="mt-2 flex flex-col sm:flex-row items-center justify-between gap-2 bg-white px-3 py-2 rounded-lg shadow flex-shrink-0" style={{ minHeight: '48px' }}>
        {data?.total > 0 ? (
          <>
          {/* Left: Records per page dropdown */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Show</span>
            <select
              value={pageSize}
              onChange={(e) => {
                patchSearchParams({
                  pageSize: e.target.value,
                  page: '1',
                });
              }}
              className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              {PAGE_SIZE_OPTIONS.map((size, index) => {
                const previousSize = index > 0 ? PAGE_SIZE_OPTIONS[index - 1] : 0;
                const isDisabled = previousSize >= data.total && size > data.total;
                return (
                  <option key={size} value={size} disabled={isDisabled}>
                    {size}
                  </option>
                );
              })}
            </select>
            <span className="text-sm text-gray-700">entries</span>
          </div>

          {/* Center: Entry count */}
          <div className="text-sm text-gray-700">
            Showing {Math.min((page - 1) * pageSize + 1, data.total)} to {Math.min(page * pageSize, data.total)} of {data.total} entries
          </div>

          {/* Right: Pagination buttons */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => patchSearchParams({ page: '1' })}
              disabled={page === 1}
              className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="First page"
            >
              &laquo;
            </button>
            <button
              onClick={() => patchSearchParams({ page: String(page - 1) })}
              disabled={page === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            {/* Page numbers */}
            {(() => {
              const totalPages = Math.ceil(data.total / pageSize);
              const pages: (number | string)[] = [];

              if (totalPages <= 7) {
                for (let i = 1; i <= totalPages; i++) pages.push(i);
              } else {
                if (page <= 3) {
                  pages.push(1, 2, 3, 4, '...', totalPages);
                } else if (page >= totalPages - 2) {
                  pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                } else {
                  pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
                }
              }

              return pages.map((p, idx) => (
                p === '...' ? (
                  <span key={`ellipsis-${idx}`} className="px-2 py-1 text-sm text-gray-500">...</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => patchSearchParams({ page: String(p) })}
                    className={`px-3 py-1 text-sm border rounded-md ${
                      page === p
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {p}
                  </button>
                )
              ));
            })()}

            <button
              onClick={() => patchSearchParams({ page: String(page + 1) })}
              disabled={page >= Math.ceil(data.total / pageSize)}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
            <button
              onClick={() =>
                patchSearchParams({ page: String(Math.ceil(data.total / pageSize)) })
              }
              disabled={page >= Math.ceil(data.total / pageSize)}
              className="px-2 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Last page"
            >
              &raquo;
            </button>
          </div>
          </>
        ) : (
          <div className="text-sm text-gray-500 w-full text-center">No records</div>
        )}
      </div>

      {/* Bulk Create Incident Modal */}
      <BulkCreateIncidentModal
        isOpen={bulkCreateModalOpen}
        onClose={handleCloseBulkModal}
        selectedIncidentIds={selectedIncidents}
        validationData={bulkValidationData}
      />

      {/* Validation Error Modal */}
      <Transition appear show={validationErrorModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setValidationErrorModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 bg-red-50">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-red-900 flex items-center">
                      <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-2" />
                      Validation Failed
                    </Dialog.Title>
                    <button
                      onClick={() => setValidationErrorModalOpen(false)}
                      className="text-gray-400 hover:text-gray-500 focus:outline-none"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="px-3 py-2">
                    <div className="mb-4">
                      <p className="text-sm text-gray-700">{validationErrors.message}</p>
                    </div>

                    {validationErrors.errors && validationErrors.errors.length > 0 && (
                      <div className="bg-red-50 border border-red-200 rounded-md p-4">
                        <h4 className="text-sm font-medium text-red-900 mb-2">Issues Found:</h4>
                        <ul className="list-disc list-inside space-y-1 text-sm text-red-800">
                          {validationErrors.errors.map((error: string, index: number) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <button
                      type="button"
                      onClick={() => setValidationErrorModalOpen(false)}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Email Ticket Modal */}
      <EmailTicketModal
        isOpen={emailModalState.isOpen}
        onClose={() => setEmailModalState({ isOpen: false, ticketId: '', ticketNumber: '', shortDescription: '' })}
        ticketId={emailModalState.ticketId}
        ticketType="INCIDENT"
        ticketNumber={emailModalState.ticketNumber}
        shortDescription={emailModalState.shortDescription}
      />

      {/* List Layout Settings Modal */}
      <ListLayoutSettingsModal
        isOpen={showLayoutSettings}
        onClose={() => setShowLayoutSettings(false)}
        entityType="INCIDENT"
        columns={layoutColumns}
        onSave={saveColumns}
        onReset={resetToDefault}
        isSaving={isLayoutSaving}
        isResetting={isLayoutResetting}
      />

      {/* Survey Confirmation Modal */}
      <SurveyConfirmationModal
        isOpen={surveyConfirmModal.isOpen}
        onSendSurvey={handleSurveyConfirmYes}
        onDontSend={handleSurveyConfirmNo}
        title="Send Survey to Customer"
        message="Do you want to send the survey to the customer?"
        isLoading={sendSurveyMutation.isPending}
        defaultToggleOn={false}
      />

      {/* Survey Result Modal (Success/Error) */}
      <Transition appear show={surveyResultModal.isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setSurveyResultModal({ isOpen: false, type: 'success', message: '' })}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        {surveyResultModal.type === 'success' ? (
                          <CheckCircleIcon className="h-6 w-6 text-green-600" />
                        ) : (
                          <XCircleIcon className="h-6 w-6 text-red-600" />
                        )}
                      </div>
                      <div>
                        <Dialog.Title as="h3" className="text-lg font-medium text-gray-900">
                          {surveyResultModal.type === 'success' ? 'Success' : 'Error'}
                        </Dialog.Title>
                      </div>
                    </div>
                    <button
                      onClick={() => setSurveyResultModal({ isOpen: false, type: 'success', message: '' })}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="mt-2 ml-9">
                    <p className="text-sm text-gray-500">
                      {surveyResultModal.message}
                    </p>
                  </div>

                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setSurveyResultModal({ isOpen: false, type: 'success', message: '' })}
                      className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${
                        surveyResultModal.type === 'success'
                          ? 'bg-green-600 hover:bg-green-700'
                          : 'bg-red-600 hover:bg-red-700'
                      }`}
                    >
                      OK
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Toast Notification */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
          <div
            className={`rounded-lg shadow-lg px-3 py-2 max-w-md ${
              toast.type === 'success'
                ? 'bg-green-50 border border-green-200'
                : toast.type === 'error'
                ? 'bg-red-50 border border-red-200'
                : 'bg-yellow-50 border border-yellow-200'
            }`}
          >
            <div className="flex items-start gap-3">
              {toast.type === 'success' && (
                <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
              )}
              {toast.type === 'error' && (
                <XMarkIcon className="h-6 w-6 text-red-600 flex-shrink-0" />
              )}
              {toast.type === 'warning' && (
                <QuestionMarkCircleIcon className="h-6 w-6 text-yellow-600 flex-shrink-0" />
              )}
              <div className="flex-1">
                <p
                  className={`text-sm font-medium ${
                    toast.type === 'success'
                      ? 'text-green-800'
                      : toast.type === 'error'
                      ? 'text-red-800'
                      : 'text-yellow-800'
                  }`}
                >
                  {toast.message}
                </p>
              </div>
              <button
                onClick={() => setToast(null)}
                className={`flex-shrink-0 ${
                  toast.type === 'success'
                    ? 'text-green-400 hover:text-green-600'
                    : toast.type === 'error'
                    ? 'text-red-400 hover:text-red-600'
                    : 'text-yellow-400 hover:text-yellow-600'
                }`}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncidentList; 